import { useNavigate } from 'react-router-dom';
import { Eye, Bug } from 'lucide-react';
import { PlatformIcon } from '@/components/common/PlatformIcon';
import { formatRelativeTime } from '@/utils/formatters';

interface DeviceCardProps {
  id: string;
  name: string;
  user: string;
  status: 'online' | 'offline';
  platform: 'iOS' | 'Android';
  appVersion: string;
  osVersion: string;
  lastActivity: string;
  sessions: number;
  errors: number;
  crashes: number;
}

export function DeviceCard({
  name,
  user,
  status,
  platform,
  appVersion,
  osVersion,
  lastActivity,
  sessions,
  errors,
  crashes,
  id,
}: DeviceCardProps) {
  const navigate = useNavigate();
  const isOnline = status === 'online';

  const handleLogsClick = () => navigate(`/logs?device=${id}`);
  const handleErrorsClick = () => navigate(`/errors?device=${id}`);

  return (
    <div className="flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card-hover">
      <div className="flex flex-col gap-3.5">
        {/* Header: ícone + nome (nome ganha a linha toda) */}
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
            <PlatformIcon platform={platform} size={20} colored />
          </span>
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-text-title">{name}</h3>
            <p className="truncate text-xs text-text-secondary">{user}</p>
          </div>
        </div>

        {/* Badges: status + plataforma */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
              isOnline ? 'bg-online/10 text-online' : 'bg-offline/10 text-offline'
            }`}
          >
            <span
              className={`size-1.5 rounded-full bg-current ${isOnline ? 'animate-pulse' : ''}`}
            />
            {isOnline ? 'Online' : 'Offline'}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-text-body">
            <PlatformIcon platform={platform} size={11} />
            {platform}
          </span>
        </div>

        {/* Versões + ID + última atividade */}
        <div className="space-y-1.5 text-xs text-text-secondary">
          <p>
            App <span className="font-medium text-text-title">{appVersion}</span>
            <span className="px-1.5 text-text-hint">·</span>
            OS <span className="font-medium text-text-title">{osVersion}</span>
          </p>
          <p className="truncate font-mono">
            ID: <span className="text-text-body">{id}</span>
          </p>
          <p>
            Última atividade:{' '}
            <span className="font-medium text-text-title">{formatRelativeTime(lastActivity)}</span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 divide-x divide-border rounded-lg bg-muted/40 py-2.5 text-center">
          <div className="px-1">
            <p className="text-base font-bold tabular-nums text-text-title">
              {sessions.toLocaleString()}
            </p>
            <p className="text-xs text-text-secondary">Sessões</p>
          </div>
          <div className="px-1">
            <p
              className={`text-base font-bold tabular-nums ${errors > 0 ? 'text-warning' : 'text-text-title'}`}
            >
              {errors}
            </p>
            <p className="text-xs text-text-secondary">Erros</p>
          </div>
          <div className="px-1">
            <p
              className={`text-base font-bold tabular-nums ${crashes > 0 ? 'text-error' : 'text-text-title'}`}
            >
              {crashes}
            </p>
            <p className="text-xs text-text-secondary">Crashes</p>
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="mt-5 flex gap-2 border-t border-border pt-4">
        <button
          onClick={handleLogsClick}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold text-text-secondary transition-colors hover:bg-muted hover:text-text-title focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <Eye size={16} /> Logs
        </button>
        <button
          onClick={handleErrorsClick}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold text-text-secondary transition-colors hover:bg-muted hover:text-text-title focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <Bug size={16} /> Erros
        </button>
      </div>
    </div>
  );
}
